<template>
  <div class="@container" :class="{ 'bg-slate-700/30 rounded-lg border border-slate-600': !embedded }">
    <div v-if="!embedded" class="flex items-center justify-between border-b border-slate-600 bg-slate-700/50 px-3 py-2">
      <h2 class="text-sm font-semibold text-white">Event Actions</h2>
      <span class="text-xs text-slate-500">{{ models.length }}</span>
    </div>
    <div class="overflow-x-auto">
      <table class="w-full" :class="{ 'min-w-[58rem] table-fixed': !embedded }">
        <colgroup v-if="!embedded">
          <col class="w-[12%]">
          <col class="w-[17%]">
          <col class="w-[21%]">
          <col class="w-[18%]">
          <col class="w-[22%]">
          <col class="w-28">
        </colgroup>
        <thead v-if="!embedded">
          <tr class="text-gray-300 text-xs uppercase tracking-wider bg-slate-800/50">
            <th class="py-3 px-4 text-left font-medium border-b border-slate-600">Event</th>
            <th class="py-3 px-4 text-left font-medium border-b border-slate-600">Condition</th>
            <th class="py-3 px-4 text-left font-medium border-b border-slate-600">Action</th>
            <th class="py-3 px-4 text-left font-medium border-b border-slate-600">Target</th>
            <th class="py-3 px-4 text-left font-medium border-b border-slate-600">Action Data</th>
            <th class="py-3 px-4 text-center font-medium border-b border-slate-600 w-16">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-700">
          <template v-for="(model, index) in models" :key="model.id">
          <tr v-if="!embedded" class="text-sm transition-colors" :class="isExpanded(model.id) ? 'bg-cyan-950/20' : 'hover:bg-slate-700/30'">
            <td class="px-3 py-2">
              <button type="button" class="flex min-w-0 items-center gap-2 text-left" :aria-expanded="isExpanded(model.id)" @click="toggleExpanded(model.id)">
                <span class="text-slate-500" :class="{ 'text-cyan-400': isExpanded(model.id) }">{{ isExpanded(model.id) ? '▾' : '▸' }}</span>
                <span class="truncate text-white" :title="eventLabel(model)">{{ eventLabel(model) }}</span>
              </button>
            </td>
            <td class="px-3 py-2 text-xs text-slate-300" :title="conditionSummary(model)">{{ conditionSummary(model) }}</td>
            <td class="px-3 py-2"><span class="block truncate text-white" :title="actionSummary(model, serviceActions)">{{ actionSummary(model, serviceActions) }}</span></td>
            <td class="px-3 py-2"><span class="block truncate" :class="targetSummary(model, node, serviceActions) === 'Primary Entity' ? 'text-slate-300' : 'font-mono text-xs text-white'" :title="targetTitle(model)">{{ targetSummary(model, node, serviceActions) }}</span></td>
            <td class="px-3 py-2 text-xs text-slate-300" :title="actionDataSummary(model)">{{ actionDataSummary(model) }}</td>
            <td class="px-3 py-2">
              <div class="flex justify-end gap-1">
                <button type="button" class="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-cyan-300" :title="isExpanded(model.id) ? 'Close editor' : 'Edit event action'" @click="toggleExpanded(model.id)">
                  <span class="sr-only">{{ isExpanded(model.id) ? 'Close editor' : 'Edit event action' }}</span>✎
                </button>
                <button type="button" class="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-cyan-300" title="Duplicate event action" @click="copyRule(model, index)">
                  <span class="sr-only">Duplicate event action</span>⧉
                </button>
                <button type="button" class="rounded p-1 text-red-400 hover:bg-red-500/10 hover:text-red-300" title="Remove event action" @click="deleteRule(model, index)">
                  <span class="sr-only">Remove event action</span>✕
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="embedded || isExpanded(model.id)" class="bg-slate-900/20 align-top">
            <td colspan="6" :class="embedded ? 'p-0' : 'border-t border-slate-700/70 px-3 py-3'">
              <div class="grid w-full grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-3">
              <div v-if="!embedded" class="min-w-0">
              <HassSelector
                v-model="model.triggers[0].event"
                :selector="triggerSelector" />
            </div>

            <div v-if="!embedded && isBtnmatrixModel(model)" class="min-w-0">
              <form-label class="text-xs text-gray-400">Button</form-label>
              <form-select
                class="w-full text-sm"
                :modelValue="buttonFilterValue(model)"
                @update:modelValue="setButtonFilter(model, $event)">
                <option value="">Any button</option>
                <option v-for="button in btnmatrixButtons" :key="button.value" :value="String(button.value)">
                  {{ button.label }}
                </option>
                <option v-if="isInvalidButtonFilter(model)" :value="String(buttonFilterValue(model))">
                  Missing button ({{ buttonFilterValue(model) }})
                </option>
              </form-select>
              <p v-if="isInvalidButtonFilter(model)" class="mt-1 text-xs text-red-400">
                Button index {{ buttonFilterValue(model) }} no longer exists in this btnmatrix.
              </p>
            </div>

            <div v-if="!embedded" class="min-w-0">
              <div class="space-y-1.5">
                <ConditionEditor
                  v-for="(cond, ci) in model.conditions" :key="cond.id"
                  :condition="cond" :runtimeVariables="runtimeVariablesFor(model)" :node="node"
                  @remove="model.conditions.splice(ci, 1)" />
                <button class="ha-builder-link-button" @click="addCondition(model)">
                  + condition
                </button>
              </div>
            </div>

            <div class="min-w-0">
              <ActionEditor
                :model="model"
                :settings="settings"
                :supportedActions="meta.supportedActions"
                :recommendedActionDomains="meta.recommendedActionDomains ?? meta.supportedActions"
                :serviceActions="serviceActions"
                :serviceCatalogueLoading="serviceCatalogueLoading"
                :serviceCatalogueError="serviceCatalogueError"
                :singleAction="embedded"
                @retry-services="loadServiceActions(true)" />
            </div>

            <div class="min-w-0">
              <template v-if="isMediaPlayerMode(model)">
                <form-label class="text-xs text-gray-400">Media player</form-label>
                <EntitySelector
                  :modelValue="mediaTargetEntity(model)"
                  @update:modelValue="setMediaEntity(model, $event)"
                  :settings="settings"
                  domain="media_player"
                  placeholder="media_player.entity" />
                <p v-if="!mediaTargetEntity(model).startsWith('media_player.')" class="mt-1 text-xs text-red-400">Media player is required.</p>
              </template>
              <template v-else-if="isActivateSceneMode(model)">
                <form-label class="text-xs text-gray-400">Scene</form-label>
                <EntitySelector
                  :modelValue="targetEntity(model)"
                  @update:modelValue="setTargetEntity(model, $event)"
                  :settings="settings"
                  domain="scene"
                  placeholder="scene.entity" />
                <p v-if="!isSceneEntityId(targetEntity(model))" class="mt-1 text-xs text-red-400">A scene entity is required.</p>
              </template>
              <CallServiceTargetPicker
                v-else-if="isAdvancedCallService(model) && showTargetSection(model)"
                :modelValue="targetEntity(model)"
                :settings="settings"
                :target="model.actions[0]?.target ?? {}"
                :service="serviceOption(model)"
                @update:target="setAdvancedTarget(model, $event)"
                @update:modelValue="setAdvancedTargetEntity(model, $event)" />
              <EntitySelector
                v-else-if="!isAdvancedCallService(model) && showTargetSection(model)"
                :modelValue="targetEntity(model)"
                @update:modelValue="setTargetEntity(model, $event)"
                :settings="settings" />
              <div v-if="!isMediaPlayerMode(model) && !isActivateSceneMode(model) && showTargetSection(model)" class="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span v-if="hasExplicitTarget(model)" class="text-cyan-300">Explicit target</span>
                <span v-else-if="targetEntity(model)" class="text-slate-500">Inherited from Primary Entity</span>
                <span v-else class="text-slate-500">No target selected</span>
                <button
                  v-if="hasExplicitTarget(model)"
                  type="button"
                  class="text-cyan-400 hover:text-cyan-300"
                  @click="usePrimaryEntityTarget(model)">
                  Use Primary Entity
                </button>
              </div>
            </div>

            <div class="min-w-0 @3xl:col-span-2">
              <ServiceFieldsEditor
                v-if="showServiceFields(model)"
                :model="model"
                :service="serviceOption(model)"
                :settings="settings"
                :runtimeVariables="runtimeVariablesFor(model)"
                :dynamicSuggestions="allSuggestions(model)" />
              <template v-if="isTtsMode(model)">
                <form-label class="text-xs text-gray-400">Text to speak</form-label>
                <TemplateDocumentEditor
                  :document="mediaDraft(model).message"
                  :settings="settings"
                  @change="commitMediaDraft(model)" />
                <p v-if="!documentHasSpokenContent(mediaDraft(model).message)" class="mt-1 text-xs text-red-400">
                  Spoken text is required.
                </p>
                <template v-if="providerOptions(model).length > 1">
                  <form-label class="mt-2 block text-xs text-gray-400">TTS provider</form-label>
                  <form-select
                    class="w-full text-sm"
                    :modelValue="mediaDraft(model).provider"
                    @update:modelValue="setMediaProvider(model, $event)">
                    <option v-for="provider in providerOptions(model)" :key="provider.mediaSourceId" :value="provider.mediaSourceId">
                      {{ provider.name }}
                    </option>
                  </form-select>
                </template>
                <form-label class="mt-2 block text-xs text-gray-400">Language</form-label>
                <form-select
                  class="w-full text-sm"
                  :modelValue="mediaDraft(model).language"
                  @update:modelValue="setMediaLanguage(model, $event)">
                  <option v-for="language in languageOptions(model)" :key="language.value" :value="language.value">
                    {{ language.label }}
                  </option>
                </form-select>
                <form-label class="mt-2 block text-xs text-gray-400">Voice</form-label>
                <form-select
                  class="w-full text-sm"
                  :modelValue="mediaDraft(model).voice"
                  :disabled="isVoiceLoading(model)"
                  @update:modelValue="setMediaVoice(model, $event)">
                  <option v-for="voice in voiceOptions(model)" :key="voice.value" :value="voice.value">
                    {{ voice.label }}
                  </option>
                </form-select>
                <p v-if="isVoiceLoading(model)" class="mt-1 text-xs text-gray-400">Loading voices…</p>
                <p v-else-if="catalogueError" class="mt-1 text-xs text-amber-400">
                  TTS catalogue unavailable. Saved values are preserved.
                </p>
                <button
                  v-if="settings.tauri"
                  class="mt-1 text-xs text-cyan-400 hover:text-cyan-300"
                  @click="loadCatalogue(true)">
                  Refresh TTS catalogue
                </button>
              </template>
              <template v-else-if="isMediaPlaybackMode(model)">
                <form-label class="text-xs text-gray-400">Source</form-label>
                <form-select
                  class="w-full text-sm"
                  :modelValue="playbackDraft(model).sourceType"
                  @update:modelValue="setPlaybackSourceType(model, $event)">
                  <option v-for="source in MEDIA_SOURCE_TYPES" :key="source.value" :value="source.value">
                    {{ source.label }}
                  </option>
                </form-select>

                <template v-if="playbackDraft(model).sourceType === 'media_source'">
                  <form-button
                    class="mt-2 w-full"
                    :disabled="!playbackDraft(model).entityId.startsWith('media_player.') || !settings.tauri"
                    @click="openMediaBrowser(model)">
                    Browse…
                  </form-button>
                  <p v-if="!settings.tauri" class="mt-1 text-xs text-amber-400">
                    Browsing requires the connected desktop app.
                  </p>
                  <div v-if="playbackDraft(model).metadata?.title || playbackDraft(model).mediaContentId" class="mt-2 rounded border border-slate-600/60 bg-slate-800/40 p-2">
                    <div class="text-xs uppercase tracking-wide text-slate-500">Selected</div>
                    <div class="truncate text-sm text-slate-200">
                      {{ playbackDraft(model).metadata?.title || playbackDraft(model).mediaContentId }}
                    </div>
                    <div class="truncate text-xs text-slate-400">{{ playbackDraft(model).mediaContentType }}</div>
                  </div>
                </template>

                <template v-else-if="playbackDraft(model).sourceType === 'url'">
                  <form-label class="mt-2 block text-xs text-gray-400">Stream / media URL</form-label>
                  <form-input
                    type="text"
                    class="w-full text-sm"
                    placeholder="https://..."
                    :modelValue="playbackDraft(model).mediaContentId"
                    @update:modelValue="setPlaybackContentId(model, $event)" />
                  <form-label class="mt-2 block text-xs text-gray-400">Content type</form-label>
                  <form-input
                    type="text"
                    class="w-full text-sm"
                    :modelValue="playbackDraft(model).mediaContentType"
                    @update:modelValue="setPlaybackContentType(model, $event)" />
                </template>

                <template v-else>
                  <form-label class="mt-2 block text-xs text-gray-400">Media content ID</form-label>
                  <form-input
                    type="text"
                    class="w-full text-sm"
                    placeholder="media-source://..."
                    :modelValue="playbackDraft(model).mediaContentId"
                    @update:modelValue="setPlaybackContentId(model, $event)" />
                  <form-label class="mt-2 block text-xs text-gray-400">Content type</form-label>
                  <form-input
                    type="text"
                    class="w-full text-sm"
                    :modelValue="playbackDraft(model).mediaContentType"
                    @update:modelValue="setPlaybackContentType(model, $event)" />
                </template>

                <p v-if="!playbackDraft(model).mediaContentId.trim()" class="mt-1 text-xs text-red-400">
                  Media content is required.
                </p>

                <label class="mt-2 flex items-center gap-2 text-xs text-gray-400">
                  <input
                    type="checkbox"
                    class="rounded border-slate-500 bg-slate-800 text-cyan-500"
                    :checked="playbackDraft(model).announce === true"
                    @change="setPlaybackAnnounce(model, $event.target.checked)" />
                  Announce
                </label>

                <form-label class="mt-2 block text-xs text-gray-400">Queue</form-label>
                <form-select
                  class="w-full text-sm"
                  :modelValue="playbackDraft(model).enqueue"
                  @update:modelValue="setPlaybackEnqueue(model, $event)">
                  <option value="">default</option>
                  <option value="play">play</option>
                  <option value="next">next</option>
                  <option value="add">add</option>
                  <option value="replace">replace</option>
                </form-select>

                <MediaBrowser
                  :show="mediaBrowserModel === model.id"
                  :settings="settings"
                  :entityId="playbackDraft(model).entityId"
                  @close="mediaBrowserModel = null"
                  @select="selectMediaItem(model, $event)" />
              </template>
              <template v-else-if="isMediaPlayerServiceMode(model)">
                <div v-for="field in mediaPlayerDataFields(model)" :key="field.parameter" class="mt-2">
                  <form-label class="block text-xs text-gray-400">{{ field.label || field.parameter }}</form-label>
                  <form-input
                    type="text"
                    class="w-full text-sm"
                    :modelValue="mediaPlayerDataValue(model, field)"
                    @update:modelValue="setMediaPlayerData(model, field.parameter, $event)" />
                </div>
              </template>
              <div v-if="!isMediaPlayerMode(model) && (extraParameters(model).length > 0 || suggestions(model).length > 0)" class="mt-2 space-y-1">
                <div v-if="!hasSelectedServiceFields(model)" class="text-xs font-semibold uppercase tracking-wide text-slate-400">Action Data</div>
                <div v-for="(p, pi) in extraParameters(model)" :key="pi" class="flex items-center gap-1">
                  <form-input type="text" placeholder="key" class="w-1/3 text-xs"
                    :modelValue="p.key" @update:modelValue="v => setExtraParameterKey(model, p, v)" />
                  <form-input type="text" placeholder="value" class="flex-1 text-xs"
                    :modelValue="p.value.toString()" @update:modelValue="v => setParamValue(model, p, v)" />
                  <button class="ha-builder-danger-button"
                    title="Remove parameter" @click="removeExtraParameter(model, p.key)">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div class="flex items-center gap-2">
                  <button class="ha-builder-link-button" @click="addExtraParameter(model)">+ custom parameter</button>
                </div>
                <form-select
                  v-if="suggestions(model).length > 0"
                  class="w-full text-xs"
                  modelValue=""
                  @update:modelValue="selectSuggestion(model, $event)">
                  <option value="">Suggestions</option>
                  <option
                    v-for="s in suggestions(model)" :key="suggestionId(s)"
                    :value="suggestionId(s)">
                    {{ s.label }}
                  </option>
                </form-select>
              </div>
            </div>
            </div>
            </td>

            <!-- Legacy delete cell removed; delete is part of the compact summary row.
              <button class="text-red-400 hover:text-red-300 transition-colors p-2 hover:bg-red-500/10 rounded"
                title="Remove rule" @click="models.splice(index, 1)">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            -->
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="!embedded" class="p-3">
      <button @click="addRule"
        class="w-full @sm:w-auto bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2">
        + Add Event Action
      </button>
    </div>
  </div>
</template>

<script setup>
/**
 * TriggerRulesTable.vue
 *
 * One row per rule AutomationModel (a single trigger, zero or more
 * conditions, one or more actions — "one or more" because ActionEditor's
 * Repeat option flattens into several call_service actions sharing the same
 * provider/service/target). Every column is driven by the widget's
 * WidgetAutomationMeta — nothing here hardcodes a widget type.
 */
import { computed, ref, reactive, watch, watchEffect, onMounted } from 'vue';
import { AutomationModel } from '../../../automation/ast/AutomationModel.js';
import { AutomationTrigger } from '../../../automation/ast/AutomationTrigger.js';
import { AutomationAction } from '../../../automation/ast/AutomationAction.js';
import { AutomationCondition } from '../../../automation/ast/AutomationCondition.js';
import { AutomationValue } from '../../../automation/ast/AutomationValue.js';
import { getParam, setParam, removeParam } from './automationParams.js';
import {
  applyActionDataSuggestion,
  getAvailableActionDataSuggestions,
  suggestionId,
} from '../../../automation/actionDataSuggestions.js';
import ConditionEditor from './ConditionEditor.vue';
import ActionEditor from './ActionEditor.vue';
import ServiceFieldsEditor from './ServiceFieldsEditor.vue';
import EntitySelector from '../EntitySelector.vue';
import HassSelector from '../HassSelector.vue';
import CallServiceTargetPicker from './CallServiceTargetPicker.vue';
import MediaBrowser from './MediaBrowser.vue';
import TemplateDocumentEditor from './TemplateDocumentEditor.vue';
import {
  buildTtsAction,
  parseTtsAction,
  canRepresentAsFriendlyTts,
  TTS_LANGUAGES,
  DEFAULT_TTS_LANGUAGE,
  DEFAULT_TTS_VOICE,
  DEFAULT_TTS_PROVIDER,
  voicesForLanguage,
} from '../../../automation/mediaPlayerTtsAction.js';
import {
  buildMediaPlaybackAction,
  parseMediaPlaybackAction,
  MEDIA_SOURCE_TYPES,
  DEFAULT_MEDIA_CONTENT_TYPE,
  mediaMetadataFromBrowseItem,
  detectMediaSourceType,
  isMediaPlayerService,
  mediaPlayerDataFields as getMediaPlayerDataFields,
} from '../../../automation/mediaPlayerAction.js';
import { setAdvancedCallServiceTarget } from '../../../automation/callServiceAction.js';
import { rememberServiceTargetContract, serviceTargetContract } from '../../../automation/serviceTargetContract.js';
import {
  createTemplateDocument,
  createTextNode,
  documentHasSpokenContent,
  validateTemplateDocument,
} from '../../../automation/templateDocument.js';
import {
  getTtsProviders,
  getTtsVoices,
  getPreferredTtsDefaults,
  savePreferredTtsDefaults,
} from '../../../services/homeAssistantService.js';
import { missingRequiredServiceFields, serviceFields, serviceOptionForAction } from './serviceFields.js';
import { getEffectivePrimaryEntity } from '../metadata/effectiveEntity.js';
import { HomeAssistantMetadataAdapter } from '../metadata/HomeAssistantMetadataAdapter.js';
import {
  actionDataSummary,
  actionSummary,
  cloneAutomationModel,
  conditionSummary,
  targetSummary,
} from './getActionPresentation.js';
import {
  btnmatrixFilterValue as getBtnmatrixFilterValue,
  findBtnmatrixButton,
  getBtnmatrixButtons,
  isValidBtnmatrixFilter,
} from '../../../automation/btnmatrixButtons.js';

const props = defineProps({
  node:     { type: Object, required: true },
  settings: { type: Object, required: true },
  models:   { type: Array,  required: true }, // AutomationModel[] — mutated directly
  meta:     { type: Object, required: true }, // WidgetAutomationMeta
  widgetId: { type: String, required: true }, // e.g. 'p1b3'
  pageId:   { type: Number, default: null },
  // Only the action editor of models[0], without table, event, button and
  // conditions: a Home Assistant flow action node edits one action this way.
  embedded: { type: Boolean, default: false },
  // The effective Primary Entity, when it is not node.hass_config's own (flow graph).
  primaryEntity: { type: String, default: null },
});
const emit = defineEmits(['validation']);
const primaryEntityOf = () => props.primaryEntity ?? getEffectivePrimaryEntity(props.node);

const mediaBrowserModel = ref(null);
const ttsProviders = ref([]);
const catalogueError = ref('');
const serviceCatalogueLoading = ref(false);
const serviceCatalogueError = ref('');
const expandedModelId = ref(null);
const voiceLists = reactive({});
const voiceLoading = reactive({});
const homeAssistantMetadata = new HomeAssistantMetadataAdapter(props.settings);
const serviceActions = ref(homeAssistantMetadata.services);

const triggerSelector = computed(() => ({
  kind: 'select',
  options: props.meta.triggers.map(event => ({
    value: event,
    label: props.meta.triggerLabels?.[event] ?? props.meta.events?.find?.(item => item.name === event)?.label ?? event,
  })),
  preserveUnknown: true,
}));

const btnmatrixButtons = computed(() => getBtnmatrixButtons(props.node));

function voiceKey(draft) {
  return `${draft.provider}|${draft.language}`;
}

function currentProvider(model) {
  const draft = mediaDraft(model);
  return ttsProviders.value.find(provider => provider.mediaSourceId === draft.provider);
}

function providerOptions(model) {
  const draft = mediaDraft(model);
  const options = [...ttsProviders.value];
  if (!options.some(provider => provider.mediaSourceId === draft.provider)) {
    options.push({
      entityId: draft.provider,
      mediaSourceId: draft.provider,
      name: `${draft.provider} (Unavailable)`,
      supportedLanguages: [],
    });
  }
  return options;
}

function languageOptions(model) {
  const draft = mediaDraft(model);
  const options = [...(currentProvider(model)?.supportedLanguages ?? [])];
  if (options.length === 0 && catalogueError.value && draft.isNew) {
    options.push(...TTS_LANGUAGES.map(({ value, label }) => ({ value, label })));
  }
  if (!options.some(language => language.value === draft.language)) {
    options.push({ value: draft.language, label: `${draft.language} (Unavailable)` });
  }
  return options;
}

function voiceOptions(model) {
  const draft = mediaDraft(model);
  let options = [...(voiceLists[voiceKey(draft)] ?? [])];
  if (options.length === 0 && catalogueError.value && draft.isNew) {
    options = voicesForLanguage(draft.language);
  }
  if (!options.some(voice => voice.value === draft.voice)) {
    options.push({ value: draft.voice, label: `${draft.voice} (Unavailable)` });
  }
  return options;
}

function isVoiceLoading(model) {
  return Boolean(voiceLoading[voiceKey(mediaDraft(model))]);
}

function isTtsMode(model) {
  const action = model.actions[0];
  return action?._editorMode === 'media_player' || canRepresentAsFriendlyTts(action);
}

function isMediaPlaybackMode(model) {
  const action = model.actions[0];
  return action?._editorMode === 'media_playback' || parseMediaPlaybackAction(action) !== null;
}

function isMediaPlayerServiceMode(model) {
  const action = model.actions[0];
  return action?.provider === 'media_player' && isMediaPlayerService(action.service) && !isMediaPlaybackMode(model) && !isTtsMode(model);
}

function isMediaPlayerMode(model) {
  return isTtsMode(model) || isMediaPlaybackMode(model) || isMediaPlayerServiceMode(model);
}

function isAdvancedCallService(model) {
  if (model.actions.length !== 1) return false;
  const action = model.actions[0];
  return action?.type === 'call_service' &&
    !['toggle', 'turn_on', 'turn_off'].includes(action.service) &&
    !isMediaPlayerMode(model) &&
    !isActivateSceneMode(model);
}

function serviceOption(model) {
  return serviceOptionForAction(model.actions[0], serviceActions.value);
}

function showServiceFields(model) {
  if (isMediaPlayerMode(model) || isActivateSceneMode(model)) return false;
  const option = serviceOption(model);
  return Boolean(option && Object.keys(serviceFields(option)).some(key => key !== 'entity_id'));
}

function targetContract(model) {
  return serviceTargetContract(model.actions[0], serviceActions.value);
}

function showTargetSection(model) {
  const contract = targetContract(model);
  return contract.supportsAny || (!contract.known && hasExplicitTarget(model));
}

function mediaDraft(model) {
  const action = model.actions[0];
  const preferred = getPreferredTtsDefaults();
  action._mediaDraft ??= parseTtsAction(action) ?? {
    entityId: primaryEntityOf(),
    operation: 'tts',
    message: createTemplateDocument([createTextNode('')]),
    provider: preferred?.provider ?? DEFAULT_TTS_PROVIDER,
    language: preferred?.language ?? DEFAULT_TTS_LANGUAGE,
    voice: preferred?.voice ?? DEFAULT_TTS_VOICE,
    isNew: true,
  };
  return action._mediaDraft;
}

function commitMediaDraft(model) {
  const draft = mediaDraft(model);
  const valid =
    draft.entityId.startsWith('media_player.') &&
    documentHasSpokenContent(draft.message) &&
    validateTemplateDocument(draft.message) &&
    draft.provider && draft.language && draft.voice;
  model.actions[0]._validationError = !valid;
  if (!valid) return;

  const action = buildTtsAction(draft);
  draft.isNew = false;
  action._editorMode = 'media_player';
  action._mediaDraft = draft;
  action._validationError = false;
  model.actions.splice(0, 1, action);
}

function setMediaEntity(model, entityId) {
  if (isMediaPlaybackMode(model)) {
    const draft = playbackDraft(model);
    draft.entityId = entityId;
    commitPlaybackDraft(model);
    return;
  }
  if (isMediaPlayerServiceMode(model)) {
    setTargetEntity(model, entityId);
    return;
  }
  mediaDraft(model).entityId = entityId;
  commitMediaDraft(model);
}

function mediaTargetEntity(model) {
  if (isMediaPlaybackMode(model)) return playbackDraft(model).entityId;
  if (isTtsMode(model)) return mediaDraft(model).entityId;
  return targetEntity(model);
}

function playbackDraft(model) {
  const action = model.actions[0];
  action._mediaDraft ??= parseMediaPlaybackAction(action) ?? {
    entityId: primaryEntityOf(),
    sourceType: 'media_source',
    mediaContentId: '',
    mediaContentType: DEFAULT_MEDIA_CONTENT_TYPE,
    announce: null,
    enqueue: '',
    isNew: true,
  };
  return action._mediaDraft;
}

function commitPlaybackDraft(model) {
  const draft = playbackDraft(model);
  const valid =
    draft.entityId.startsWith('media_player.') &&
    draft.mediaContentId.trim() &&
    draft.mediaContentType.trim();
  model.actions[0]._validationError = !valid;
  if (!valid) return;

  const action = buildMediaPlaybackAction(draft);
  draft.isNew = false;
  action._editorMode = 'media_playback';
  action._mediaDraft = draft;
  action._validationError = false;
  model.actions.splice(0, 1, action);
}

function setPlaybackSourceType(model, sourceType) {
  const draft = playbackDraft(model);
  if (sourceType !== detectMediaSourceType(draft.mediaContentId)) {
    draft.mediaContentId = '';
    draft.metadata = {};
  }
  draft.sourceType = sourceType;
  commitPlaybackDraft(model);
}

function setPlaybackContentId(model, value) {
  playbackDraft(model).mediaContentId = value;
  commitPlaybackDraft(model);
}

function setPlaybackContentType(model, value) {
  playbackDraft(model).mediaContentType = value;
  commitPlaybackDraft(model);
}

function setPlaybackAnnounce(model, value) {
  playbackDraft(model).announce = value ? true : null;
  commitPlaybackDraft(model);
}

function setPlaybackEnqueue(model, value) {
  playbackDraft(model).enqueue = value;
  commitPlaybackDraft(model);
}

function openMediaBrowser(model) {
  if (!playbackDraft(model).entityId.startsWith('media_player.') || !props.settings.tauri) return;
  mediaBrowserModel.value = model.id;
}

function selectMediaItem(model, { item, navigateIds = [] }) {
  const draft = playbackDraft(model);
  draft.mediaContentId = item.media_content_id ?? '';
  draft.mediaContentType = item.media_content_type || DEFAULT_MEDIA_CONTENT_TYPE;
  draft.metadata = mediaMetadataFromBrowseItem(item, navigateIds);
  draft.sourceType = detectMediaSourceType(draft.mediaContentId);
  commitPlaybackDraft(model);
  mediaBrowserModel.value = null;
}

function mediaPlayerDataFields(model) {
  return getMediaPlayerDataFields(model.actions[0]?.service);
}

function mediaPlayerDataValue(model, field) {
  const value = model.actions[0]?.data?.[field.parameter];
  const parameter = getParam(model.actions[0]?.parameters ?? [], field.parameter);
  return value == null ? parameter?.value?.toString() ?? field.value ?? '' : String(value);
}

function setMediaPlayerData(model, key, value) {
  const action = model.actions[0];
  action.data = { ...(action.data ?? {}), [key]: value };
}

async function loadVoices(model, selectDefault = false) {
  const draft = mediaDraft(model);
  const provider = currentProvider(model);
  if (!provider || !props.settings.tauri) return;
  const key = voiceKey(draft);
  voiceLoading[key] = true;
  try {
    const response = await getTtsVoices(props.settings, provider.entityId, draft.language);
    voiceLists[key] = response.voices ?? [];
    if (selectDefault || !draft.voice) {
      draft.voice = response.defaultVoice ?? provider.defaultVoice ?? voiceLists[key][0]?.value ?? draft.voice;
      commitMediaDraft(model);
    }
  } catch (error) {
    catalogueError.value = String(error);
  } finally {
    voiceLoading[key] = false;
  }
}

async function setMediaProvider(model, providerId) {
  const draft = mediaDraft(model);
  const provider = ttsProviders.value.find(option => option.mediaSourceId === providerId);
  if (!provider) return;
  draft.provider = providerId;
  draft.language = provider.defaultLanguage ?? provider.supportedLanguages[0]?.value ?? DEFAULT_TTS_LANGUAGE;
  draft.voice = provider.defaultVoice ?? '';
  await loadVoices(model, true);
  commitMediaDraft(model);
  savePreferredTtsDefaults(draft);
}

async function setMediaLanguage(model, language) {
  const draft = mediaDraft(model);
  draft.language = language;
  draft.voice = '';
  await loadVoices(model, true);
  commitMediaDraft(model);
  savePreferredTtsDefaults(draft);
}

function setMediaVoice(model, voice) {
  const draft = mediaDraft(model);
  if (!voiceOptions(model).some(option => option.value === voice)) return;
  draft.voice = voice;
  commitMediaDraft(model);
  savePreferredTtsDefaults(draft);
}

async function applyPreferredDefaults(model) {
  const draft = mediaDraft(model);
  const preferred = getPreferredTtsDefaults();
  const provider = ttsProviders.value.find(option =>
    option.mediaSourceId === preferred?.provider
  ) ?? ttsProviders.value[0];
  if (!provider) return;

  draft.provider = provider.mediaSourceId;
  const supportedLanguages = provider.supportedLanguages ?? [];
  draft.language = supportedLanguages.some(option => option.value === preferred?.language)
    ? preferred.language
    : provider.defaultLanguage ?? supportedLanguages[0]?.value ?? DEFAULT_TTS_LANGUAGE;
  draft.voice = provider.mediaSourceId === preferred?.provider &&
    draft.language === preferred?.language
    ? preferred.voice
    : provider.defaultVoice ?? '';
  await loadVoices(model, !draft.voice);
  commitMediaDraft(model);
}

async function loadCatalogue(refresh = false) {
  if (!props.settings.tauri) {
    catalogueError.value = 'Home Assistant catalogue requires the desktop connection.';
    return;
  }
  try {
    ttsProviders.value = await getTtsProviders(props.settings, { refresh });
    catalogueError.value = '';
    for (const model of props.models.filter(isTtsMode)) {
      const draft = mediaDraft(model);
      if (draft.isNew && ttsProviders.value.length > 0) {
        await applyPreferredDefaults(model);
      } else {
        await loadVoices(model, false);
      }
    }
  } catch (error) {
    catalogueError.value = String(error);
  }
}

onMounted(() => {
  loadCatalogue();
  loadServiceActions();
});

watch(
  () => [
    props.settings.tauri,
    props.settings.hass?.protocol,
    props.settings.hass?.host,
    props.settings.hass?.port,
    props.settings.hass?.token,
  ],
  () => {
    loadCatalogue(true);
    loadServiceActions(true);
  },
);

async function loadServiceActions(refresh = false) {
  serviceCatalogueLoading.value = true;
  homeAssistantMetadata.setSettings(props.settings);
  if (!props.settings.tauri) {
    serviceActions.value = homeAssistantMetadata.services;
    serviceCatalogueError.value = 'Home Assistant actions are unavailable; the saved value is preserved.';
    serviceCatalogueLoading.value = false;
    return;
  }
  try {
    serviceActions.value = await homeAssistantMetadata.loadServices({ refresh });
    for (const model of props.models) {
      for (const action of model.actions ?? []) {
        const serviceId = action.provider && action.service
          ? `${action.provider}.${action.service}`
          : action.service;
        const option = serviceActions.value.find(item => item.value === serviceId);
        if (option) rememberServiceTargetContract(action, option);
      }
    }
    serviceCatalogueError.value = homeAssistantMetadata.lastServiceError
      ? 'Home Assistant actions are unavailable; the saved value is preserved.'
      : '';
  } catch {
    serviceCatalogueError.value = 'Home Assistant actions are unavailable; the saved value is preserved.';
  } finally {
    serviceCatalogueLoading.value = false;
  }
}

watchEffect(() => {
  if (ttsProviders.value.length === 0) return;
  for (const model of props.models.filter(isTtsMode)) {
    const draft = mediaDraft(model);
    if (!draft.isNew || draft.catalogueInitialized) continue;
    draft.catalogueInitialized = true;
    applyPreferredDefaults(model);
  }
});

watchEffect(() => {
  const hasInvalidSceneTarget = props.models.some(model =>
    isActivateSceneMode(model) && !isSceneEntityId(targetEntity(model))
  );
  const hasMissingServiceField = props.models.some(model => {
    const option = serviceOption(model);
    return model.actions.some(action => missingRequiredServiceFields(action, option).length > 0);
  });
  const hasInvalidButtonFilter = props.models.some(model =>
    isBtnmatrixModel(model) && !isValidBtnmatrixFilter(model.triggers[0]) ||
    isBtnmatrixModel(model) && getBtnmatrixFilterValue(model.triggers[0]) !== null &&
      !findBtnmatrixButton(props.node, getBtnmatrixFilterValue(model.triggers[0]))
  );
  emit('validation', !hasInvalidSceneTarget && !hasMissingServiceField &&
    !hasInvalidButtonFilter &&
    !props.models.some(model => model.actions.some(action => action._validationError)));
});

function eventLabel(model) {
  const event = model.triggers[0]?.event;
  const label = (props.meta.triggerLabels?.[event] ?? props.meta.events?.find?.(item => item.name === event)?.label ?? event) || '—';
  if (!isBtnmatrixModel(model)) return label;
  const filter = getBtnmatrixFilterValue(model.triggers[0]);
  if (filter === null) return `${label} · Any button`;
  const button = findBtnmatrixButton(props.node, filter);
  return `${label} · ${button?.label ?? `Missing button (${filter})`}`;
}

function isBtnmatrixModel(model) {
  return props.node.obj === 'btnmatrix' && model?.triggers?.[0]?.source === 'widget';
}

function buttonFilterValue(model) {
  const value = getBtnmatrixFilterValue(model.triggers[0]);
  return value === null ? '' : String(value);
}

function isInvalidButtonFilter(model) {
  if (!isBtnmatrixModel(model)) return false;
  const value = getBtnmatrixFilterValue(model.triggers[0]);
  return value !== null && (!isValidBtnmatrixFilter(model.triggers[0]) || !findBtnmatrixButton(props.node, value));
}

function setButtonFilter(model, value) {
  const trigger = model.triggers[0];
  if (!trigger) return;
  trigger.filter = value === '' ? null : { val: Number(value) };
}

function targetTitle(model) {
  const summary = targetSummary(model, props.node, serviceActions.value);
  if (summary === 'Primary Entity') return primaryEntityOf() || summary;
  return summary;
}

function isExpanded(modelId) {
  return expandedModelId.value === modelId;
}

function toggleExpanded(modelId) {
  expandedModelId.value = isExpanded(modelId) ? null : modelId;
}

function copyRule(model, index) {
  const copy = cloneAutomationModel(model);
  props.models.splice(index + 1, 0, copy);
  expandedModelId.value = copy.id;
}

function deleteRule(model, index) {
  if (expandedModelId.value === model.id) expandedModelId.value = null;
  props.models.splice(index, 1);
}

function addRule() {
  const action = new AutomationAction({ type: 'call_service', service: '' });
  delete action.provider;
  const model = new AutomationModel({
    widgetPageId: props.pageId,
    widgetHaspId: props.node.haspid,
    widgetType:   props.node.obj,
    triggers:     [new AutomationTrigger({ source: 'widget', event: props.meta.triggers[0], widgetId: props.widgetId })],
    actions:      [action],
  });
  props.models.push(model);
  expandedModelId.value = model.id;
}

function addCondition(model) {
  const firstVar = props.meta.runtimeVariables[0];
  model.conditions.push(new AutomationCondition({
    left:     firstVar ? AutomationValue.reference(firstVar.name) : AutomationValue.reference(''),
    operator: '==',
    right:    AutomationValue.literal(''),
  }));
}

function runtimeVariablesFor(model) {
  const eventName = model.triggers[0]?.event;
  const event = props.meta.events?.find(item => item.name === eventName);
  if (event?.payloadMetadataDefined) return event.payloadVariables ?? [];
  if (!event?.payloadNames?.length) return props.meta.runtimeVariables ?? [];
  const names = new Set(event.payloadNames);
  return (props.meta.runtimeVariables ?? []).filter(variable => names.has(variable.name));
}

// ── Target (entity_id) — kept in sync across every action in the row ───────

function targetEntity(model) {
  const action = model.actions[0];
  const entityId = action?.target?.entity_id;
  if (Array.isArray(entityId) && entityId.length > 0) return entityId.join(', ');
  if (typeof entityId === 'string' && entityId) return entityId;
  const parameterEntity = getParam(action?.parameters ?? [], 'entity_id')?.value?.toString();
  if (parameterEntity) return parameterEntity;
  return targetContract(model).entity ? primaryEntityOf() : '';
}

function hasExplicitTarget(model) {
  return model.actions.some(action => {
    const target = action?.target;
    const hasTargetField = target && Object.entries(target).some(([key, value]) =>
      key !== 'source' && value !== '' && value != null && (!Array.isArray(value) || value.length > 0)
    );
    const parameter = getParam(action?.parameters ?? [], 'entity_id');
    return Boolean(hasTargetField || parameter?.value?.toString?.());
  });
}

/** Remove an intentional target override without touching action data. */
function usePrimaryEntityTarget(model) {
  for (const action of model.actions) {
    if (action.target) {
      const nextTarget = { ...action.target };
      delete nextTarget.entity_id;
      delete nextTarget.device_id;
      delete nextTarget.area_id;
      delete nextTarget.source;
      action.target = Object.keys(nextTarget).length ? nextTarget : null;
    }
    removeParam(action.parameters ?? [], 'entity_id');
  }
}

function isActivateSceneMode(model) {
  const action = model.actions[0];
  return action?.provider === 'scene' && action.service === 'turn_on';
}
function isSceneEntityId(entityId) {
  return /^scene\.[a-z0-9_]+$/.test(entityId);
}
function setTargetEntity(model, value) {
  for (const action of model.actions) {
    if (action.target != null) {
      action.target = { ...action.target, entity_id: value };
      delete action.target.source;
    } else {
      setParam(action.parameters, 'entity_id', value);
    }
  }
}
function setAdvancedTargetEntity(model, entityId) {
  for (const action of model.actions) setAdvancedCallServiceTarget(action, entityId);
}
function setAdvancedTarget(model, target) {
  for (const action of model.actions) setAdvancedCallServiceTarget(action, target);
}

// ── Parameters (everything except entity_id) ────────────────────────────────

function extraParameters(model) {
  const fields = new Set(Object.keys(serviceFields(serviceOption(model))));
  return (model.actions[0]?.parameters ?? []).filter(p => p.key !== 'entity_id' && !fields.has(p.key));
}
function hasSelectedServiceFields(model) {
  const fields = new Set(Object.keys(serviceFields(serviceOption(model))));
  return model.actions.some(action => (action.parameters ?? []).some(parameter => fields.has(parameter.key)));
}
function addExtraParameter(model) {
  for (const action of model.actions) action.parameters.push({ key: '', value: AutomationValue.literal('') });
}
function removeExtraParameter(model, key) {
  for (const action of model.actions) removeParam(action.parameters, key);
}
function setParamValue(model, param, value) {
  for (const action of model.actions) setParam(action.parameters, param.key, value);
}
function setExtraParameterKey(model, param, value) {
  const key = String(value ?? '');
  const known = new Set(Object.keys(serviceFields(serviceOption(model))));
  if (known.has(key)) return;
  param.key = key;
}
function suggestions(model) {
  return allSuggestions(model).filter(suggestion => {
    const selected = new Set(model.actions.flatMap(action =>
      (action.parameters ?? []).map(parameter => parameter.key).filter(Boolean)));
    return !selected.has(suggestion.parameter);
  });
}
function allSuggestions(model) {
  return getAvailableActionDataSuggestions(
    model.actions[0],
    serviceOption(model),
    props.node,
    model.triggers[0]?.event ?? '',
  );
}
function selectSuggestion(model, id) {
  const suggestion = suggestions(model).find(item => suggestionId(item) === id);
  applyActionDataSuggestion(model.actions, suggestion);
}
</script>
